import { useMemo } from 'react';

import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { getVisibleCommentThreads } from '@confluence/comments-panel-utils/entry-points/getVisibleCommentThreads.ts';
import { getAnnotationThreadKeys } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import type {
	CommentData,
	CommentType,
	ReplyData,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { useCommentsDataState } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type { SitePermissionType } from '@confluence/comments-data-manager/entry-points/useAllCommentsDataQueryTypes.ts';
import { useProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';

export const useFooterComments = (): {
	footerCommentIds: string[];
	footerCommentThreads: (
		| CommentData
		| ({
				readonly author: {
					readonly __typename: string;
					readonly accountId?: string | null;
					readonly displayName: string | null;
					readonly permissionType: SitePermissionType | null;
					readonly profilePicture: {
						readonly path: string;
					} | null;
					readonly type: string | null;
				};
				readonly body: {
					readonly value: string;
				};
				readonly container: {
					readonly id: string | null;
				};
				readonly contentStatus: string;
				readonly contributors: ReadonlyArray<{
					readonly __typename: string;
					readonly accountId?: string | null;
					readonly displayName: string | null;
					readonly profilePicture: {
						readonly path: string;
					} | null;
				} | null> | null;
				readonly createdAt: {
					readonly value: string;
				};
				readonly createdAtNonLocalized: string;
				readonly id: string;
				readonly links: {
					readonly editui: string | null;
					readonly webui: string | null;
				};
				// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
				readonly location: {
					readonly __typename: string;
					readonly commentResolveProperties?: {
						readonly resolved: boolean;
						readonly resolvedFriendlyDate: string | null;
						readonly resolvedTime: AGG$Long | null;
						readonly resolvedUser: string | null;
					} | null;
					readonly inlineMarkerRef?: string | null;
					readonly inlineResolveProperties?: {
						readonly isDangling: boolean;
						readonly resolved: boolean;
						readonly resolvedFriendlyDate: string | null;
						readonly resolvedTime: AGG$Long | null;
						readonly resolvedUser: string | null;
					} | null;
					readonly inlineText?: string | null;
					readonly type: string;
				};
				readonly parentId: string | null;
				readonly permissions: {
					readonly isEditable: boolean;
					readonly isRemovable: boolean;
					readonly isResolvable: boolean;
				};
				readonly reactionsSummary: {
					readonly ari: string;
					readonly containerAri: string;
					readonly reactionsCount: number;
					readonly reactionsSummaryForEmoji: ReadonlyArray<{
						readonly count: number;
						readonly emojiId: string;
						readonly id: string;
						readonly reacted: boolean;
					} | null>;
				} | null;
				readonly replies: ReadonlyArray<{
					readonly author: {
						readonly __typename: string;
						readonly accountId?: string | null;
						readonly displayName: string | null;
						readonly permissionType: SitePermissionType | null;
						readonly profilePicture: {
							readonly path: string;
						} | null;
					};
					readonly body: {
						readonly value: string;
					};
					readonly contentStatus: string;
					readonly contributors: ReadonlyArray<{
						readonly __typename: string;
						readonly accountId?: string | null;
						readonly displayName: string | null;
						readonly profilePicture: {
							readonly path: string;
						} | null;
					} | null> | null;
					readonly createdAt: {
						readonly value: string;
					};
					readonly createdAtNonLocalized: string;
					readonly id: string;
					readonly links: {
						readonly editui: string | null;
						readonly webui: string | null;
					};
					// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
					readonly location: {
						readonly __typename: string;
						readonly commentResolveProperties?: {
							readonly resolved: boolean;
							readonly resolvedFriendlyDate: string | null;
							readonly resolvedTime: AGG$Long | null;
							readonly resolvedUser: string | null;
						} | null;
						readonly inlineMarkerRef?: string | null;
						readonly inlineResolveProperties?: {
							readonly isDangling: boolean;
							readonly resolved: boolean;
							readonly resolvedFriendlyDate: string | null;
							readonly resolvedTime: AGG$Long | null;
							readonly resolvedUser: string | null;
						} | null;
						readonly inlineText?: string | null;
						readonly type: string;
					};
					readonly parentId: string | null;
					readonly permissions: {
						readonly isEditable: boolean;
						readonly isRemovable: boolean;
						readonly isResolvable: boolean;
					};
					readonly reactionsSummary: {
						readonly ari: string;
						readonly containerAri: string;
						readonly reactionsCount: number;
						readonly reactionsSummaryForEmoji: ReadonlyArray<{
							readonly count: number;
							readonly emojiId: string;
							readonly id: string;
							readonly reacted: boolean;
						} | null>;
					} | null;
					readonly version: {
						readonly by: {
							readonly __typename: string;
							readonly displayName: string | null;
						} | null;
						readonly number: number | null;
						readonly when: string | null;
					};
				} | null>;
				readonly version: {
					readonly by: {
						readonly __typename: string;
						readonly accountId?: string | null;
						readonly displayName: string | null;
						readonly type: string | null;
					} | null;
					readonly friendlyWhen: string | null;
					readonly number: number | null;
					readonly when: string | null;
				};
		  } & {
				type: CommentType;
				isOpen: boolean;
				replies: ReplyData[];
		  })
	)[];
} => {
	const { orderedActiveAnnotationIdList } = useCommentsDataState();

	const pageMode = useGetPageMode();

	const { commentsDataMap } = useProcessedCommentsDataState();

	return useMemo(() => {
		const footerCommentThreads = getVisibleCommentThreads({
			includeGeneralComments: true,
			commentsDataMap,
			includeInlineComments: false,
			orderedAnnotationIds: getAnnotationThreadKeys(orderedActiveAnnotationIdList),
			pageMode,
		});

		const footerCommentIds = footerCommentThreads.map((thread) => thread.id);

		return {
			footerCommentIds,
			footerCommentThreads,
		};
	}, [commentsDataMap, orderedActiveAnnotationIdList, pageMode]);
};
